<template>
    <div class="bg-lightest reservations-panel py-4 mt-0 reservations-min-height">
        <div class="options d-flex align-items-center my-3 pl-3">
            <div class="col-sm-12">
                <div class="row">
                    <div class="col-sm-12">
                        <div class="row">
                            <div class="d-flex col-sm-4 pl-0">
                                <div class="d-flex w-100">
                                    <input class="bg-lighter flexGrow1" type="text" v-model="filters.searchString" placeholder="Search customer, ticket, voucher">
                                    <button class="btn wider blue ml-3 w-80" @click="onFilterChange">Search</button>
                                </div>
                            </div>

                            <div class="col-sm-4 d-flex pl-0 align-items-center">
                                <!-- spacer: date range is controlled by the Navigator above -->
                            </div>

                            <div class="col-sm-4 d-flex justify-content-end align-items-center pr-0">
                                <export :exportOptions="exportOptions"></export>
                            </div>
                        </div>
                    </div>

                    <div class="col-sm-12 mt-3">
                        <div class="row d-flex align-items-start">
                            <div class="d-flex flexGrow1">
                                <!-- All Products -->
                                <div class="col-md-2 d-flex flex-row align-items-center pl-0">
                                    <multiselect
                                        v-model="filters.productId"
                                        :options="productOptions"
                                        :multiple="true"
                                        :close-on-select="false"
                                        :clear-on-select="false"
                                        :preserve-search="false"
                                        placeholder="All Products"
                                        label="name"
                                        track-by="id"
                                        selectLabel=""
                                        deselectLabel=""
                                        selectedLabel=""
                                        class="w-100"
                                        @input="onFilterChange"
                                    >
                                        <template slot="selection" slot-scope="{ values }">
                                            <span class="multiselect__single" v-if="values.length > 1">{{ values.length }} products selected</span>
                                            <span class="multiselect__single" v-else-if="values.length == 1">{{values[0].name}}</span>
                                        </template>
                                        <template slot="option" slot-scope="props">
                                            <i v-if="filters.productId.filter(i => i.id == props.option.id).length" class="far fa-check-square"></i>
                                            <i v-else class="far fa-square"></i>
                                            <span>{{props.option.name}}</span>
                                        </template>
                                    </multiselect>
                                </div>

                                <!-- All Sellers -->
                                <div class="col-md-2 d-flex flex-row align-items-center pl-0">
                                    <seller-picker
                                        ref="sellerPicker"
                                        class="w-100"
                                        placeholder="All Sellers"
                                        @input="sellerChanged"
                                        @deselect="deselectSeller"
                                        :optionsType="'new'">
                                    </seller-picker>
                                </div>

                                <!-- Status -->
                                <div class="col-md-2 d-flex flex-row align-items-center pl-0">
                                    <multiselect
                                        v-model="filters.status"
                                        :options="statusOptions"
                                        :multiple="false"
                                        :close-on-select="true"
                                        placeholder="Status"
                                        label="name"
                                        track-by="id"
                                        selectLabel=""
                                        deselectLabel=""
                                        selectedLabel=""
                                        class="w-100"
                                        @input="onFilterChange"
                                    />
                                </div>
                            </div>

                            <div>
                                <div class="d-flex flex-row align-items-center pr-3">
                                    <button class="btn tertiary reservations-filters-height" @click="clearFilters">Clear All</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-sm-12 d-flex justify-content-between my-4" v-if="hasLoadedOnce">
            <div class="bold dark">
                <span>Results per page:</span>
                <select v-on:change="onFilterChange" v-model="resultsPerPage" class="results-per-page border-light">
                    <option v-for="option in resultsPerPageOptions" :key="option.pSize" :value="option.pSize">{{ option.pSizeText }}</option>
                </select>
            </div>
            <uib-pagination
                v-if="totalItems > resultsPerPage"
                v-model="pagination"
                :total-items="totalItems"
                :items-per-page="resultsPerPage"
                :boundary-link-numbers="false"
                :max-size="7"
                @change="fetchPaymentLinks"
            ></uib-pagination>
        </div>

        <list
            v-if="!isLoading"
            class="px-3"
            :header="header"
            :body="body"
            :rows="allPaymentLinks"
            :showNoDataMessage="false"
        ></list>

        <div v-if="isLoading" class="p-4 my-5 text-center">
            <loader></loader>
        </div>

        <div v-if="!isLoading && hasLoadedOnce && allPaymentLinks.length === 0" class="text-center p-4 mt-5">
            <span class="dark font-weight-600 fs-large">No payment links were found</span>
        </div>

        <div class="col-sm-12 d-flex justify-content-end my-4" v-if="totalItems > resultsPerPage">
            <uib-pagination
                v-model="pagination"
                :total-items="totalItems"
                :items-per-page="resultsPerPage"
                :boundary-link-numbers="false"
                :max-size="7"
                @change="fetchPaymentLinks"
            ></uib-pagination>
        </div>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import Export from '@/tools/ExportV2'
import SellerPicker from '@/tools/SellerPicker'
import Loader from '@/tools/loaders/loader1'
import moment from 'moment'
import { eventHub } from '@/main'
import debounce from 'lodash/debounce'
import { mapGetters, mapActions } from 'vuex'
import { PAYMENT_LINK_STATUS } from '@/enums/paymentLinkStatus'
import { SELLER_CATEGORY_TYPE } from '@/enums/sellerCategoryType'
import { RESERVATION_LIST_TYPE } from '@/enums/reservationListType'
import { RESULTS_PER_PAGE_OPTIONS } from '@/utils/resultsPerPageOptions'
import List from '@/tools/list/baseList'
import paymentLinksService from '@/api/reservation/paymentLinksService'

export default {
    name: 'PaymentLinks',
    components: { Multiselect, Export, SellerPicker, List, Loader },
    data() {
        return {
            filters: {
                searchString: null,
                productId: [],
                sellerId: null,
                sellerType: null,
                channelType: null,
                status: null,
                dateRange: [
                    moment().format('YYYY-MM-DD'),
                    moment().add(365, 'days').format('YYYY-MM-DD'),
                ],
                filterDateType: RESERVATION_LIST_TYPE.DEPARTURE_DATE
            },
            allPaymentLinks: [],
            isLoading: false,
            hasLoadedOnce: false,
            totalItems: 0,
            pagination: { currentPage: 1 },
            resultsPerPage: 20,
            resultsPerPageOptions: RESULTS_PER_PAGE_OPTIONS,
            exportOptions: [
                { label: 'Excel', action: () => this.exportExcel() }
            ],
            statusOptions: [
                { id: PAYMENT_LINK_STATUS.PENDING, name: 'Pending' },
                { id: PAYMENT_LINK_STATUS.PAID, name: 'Paid' },
                { id: PAYMENT_LINK_STATUS.CANCELLED, name: 'Canceled' },
                { id: PAYMENT_LINK_STATUS.EXPIRED, name: 'Expired' }
            ]
        }
    },
    created() {
        this._onDateRangeChanged = ({ dateRange, filterDateType }) => {
            this.filters.dateRange = dateRange;
            this.filters.filterDateType = filterDateType;
            this.onFilterChange();
        };
        eventHub.$on('paymentLinks:dateRangeChanged', this._onDateRangeChanged);
        eventHub.$on('paymentLinks:refresh', this.fetchPaymentLinks);
        eventHub.$emit('paymentLinks:requestDateRange');
        this.fetchPaymentLinks();
    },
    beforeDestroy() {
        eventHub.$off('paymentLinks:dateRangeChanged', this._onDateRangeChanged);
        eventHub.$off('paymentLinks:refresh', this.fetchPaymentLinks);
    },
    computed: {
        ...mapGetters(['getSlimFilters']),
        productOptions() {
            return this.getSlimFilters && this.getSlimFilters.productList || [];
        },
        header() {
            return {
                list: {
                    reservation: 'Reservation',
                    product: 'Product',
                    clientName: 'Client name',
                    seller: 'Seller',
                    expiration: 'Expiration date',
                    amount: 'Amount',
                    status: 'Status'
                },
                customClassCell: { status: 'w-1 white-space-nowrap' }
            };
        },
        body() {
            return {
                customCells: {
                    reservation: this.cellReservation,
                    product: this.cellProduct,
                    clientName: this.cellClientName,
                    seller: this.cellSeller,
                    expiration: this.cellExpiration,
                    amount: this.cellAmount,
                    status: this.cellStatus
                },
                customRowEvent: this.onRowClick,
                customClassCell: { status: 'w-1 white-space-nowrap' }
            };
        },
        requestParams() {
            return {
                Products: this.filters.productId.length ? this.filters.productId.map(p => p.id) : null,
                SellerId: this.filters.sellerId,
                SellerType: this.filters.sellerType,
                ChannelType: this.filters.channelType,
                Status: this.filters.status ? this.filters.status.id : null,
                Search: this.filters.searchString || null,
                Page: this.pagination.currentPage,
                PageSize: this.resultsPerPage,
                StartDate: this.filters.dateRange[0],
                EndDate: this.filters.dateRange[1],
                FilterDateType: this.filters.filterDateType,
            };
        }
    },
    methods: {
        ...mapActions(['openModal']),
        fetchPaymentLinks: debounce(async function() {
            this.isLoading = true;

            const result = await paymentLinksService.listPaymentLinks(this.requestParams);

            if (result.ok) {
                this.allPaymentLinks = result.value.items;
                this.totalItems = result.value.totalCount;
            }

            this.isLoading = false;
            this.hasLoadedOnce = true;
        }, 500),
        onFilterChange() {
            this.pagination.currentPage = 1;
            this.fetchPaymentLinks();
        },
        sellerChanged(seller) {
            const isCategory = seller.id in SELLER_CATEGORY_TYPE;
            this.filters.sellerId = isCategory ? null : seller.id;
            this.filters.sellerType = isCategory ? SELLER_CATEGORY_TYPE[seller.id] : null;
            this.filters.channelType = isCategory ? null : seller.type;
            this.onFilterChange();
        },
        deselectSeller() {
            this.filters.sellerId = null;
            this.filters.sellerType = null;
            this.filters.channelType = null;
            this.onFilterChange();
        },
        clearFilters() {
            this.filters = {
                searchString: null,
                productId: [],
                sellerId: null,
                sellerType: null,
                channelType: null,
                status: null,
                dateRange: this.filters.dateRange,
                filterDateType: this.filters.filterDateType,
            };
            if (this.$refs.sellerPicker) {
                this.$refs.sellerPicker.deselectSeller();
            }
            this.onFilterChange();
        },
        async exportExcel() {
            const result = await paymentLinksService.exportPaymentLinks(this.requestParams);
            if (!result.ok) return;

            window.URL = window.URL || window.webkitURL;
            const link = document.createElement('a');
            document.body.appendChild(link);
            link.href = window.URL.createObjectURL(result.value.blob);
            link.download = result.value.filename;
            link.click();
            URL.revokeObjectURL(link.href);
            document.body.removeChild(link);
        },
        async onRowClick(row) {
            const result = await paymentLinksService.getPaymentLink(row.id);
            if (!result.ok) return;
            this.openModal('PaymentLinkDetails');
            this.$nextTick(() => eventHub.$emit('paymentLinks:detailsLoaded', { ...result.value, reservation: row.reservation }));
        },
        cellReservation(row) {
            return `<div><b>${row.reservation}</b></div><div class="sub">${row.product}</div>`;
        },
        cellProduct(row) { return row.product; },
        cellClientName(row) { return row.clientName || ''; },
        cellSeller(row) { return row.seller || ''; },
        cellExpiration(row) {
            if (!row.cancelBookingIfNotPaidAfter) return '';
            const parsed = moment(row.cancelBookingIfNotPaidAfter);
            return `<div>${parsed.format('DD.MM.YYYY')}</div><div>${parsed.format('HH:mm')}</div>`;
        },
        cellAmount(row) {
            return row.value != null ? `${row.value.toFixed(2)} €` : '';
        },
        cellStatus(row) {
            const STATUS_CONFIG = {
                [PAYMENT_LINK_STATUS.PENDING]: { label: 'Pending', cssClass: 'bg-color-1' },
                [PAYMENT_LINK_STATUS.PAID]: { label: 'Paid', cssClass: 'bg-color-3' },
                [PAYMENT_LINK_STATUS.CANCELLED]: { label: 'Canceled', cssClass: 'bg-color-6' },
                [PAYMENT_LINK_STATUS.EXPIRED]: { label: 'Expired', cssClass: 'bg-color-5' },
            };

            const config = STATUS_CONFIG[row.status];

            return `<div class="lightest fs-smallest px-2 py-1 text-uppercase ${config.cssClass} radius-stadium text-center font-weight-600">${config.label}</div>`;
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '../../assets/scss/_variables.scss';
    .reservations-filters-height { height: 2.399rem; }
</style>


// WEBPACK FOOTER //
// PaymentLinks.vue?96bc7d9e